import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  QrCode, Car, Wrench, Play, ChevronRight, CheckCircle2, 
  Gauge, Cog, FileText, Video, ArrowRight, Sparkles,
  Info, Clock, AlertTriangle, Star, Shield, Zap
} from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';

/**
 * Premium Features Showcase Component
 * 
 * Marketing-optimierte Darstellung der Premium-Features:
 * - VIN Decoder
 * - Technische Details
 * - Step-by-Step Reparaturanleitungen
 * - Video-Guides
 */
export const PremiumFeaturesShowcase = ({ onCtaClick }) => {
  const [activeFeature, setActiveFeature] = useState('vin');
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);

  // Feature-Tabs
  const featureTabs = [
    { id: 'vin', icon: QrCode, label: 'VIN Decoder', color: '#1EA0E6' },
    { id: 'specs', icon: Gauge, label: 'Tech-Daten', color: '#22C55E' },
    { id: 'repair', icon: Wrench, label: 'Reparatur-Guide', color: '#F59E0B' },
    { id: 'video', icon: Video, label: 'Video-Anleitung', color: '#EF4444' }
  ];

  // VIN Decoder Demo-Daten
  const vinDecoderDemo = {
    vin: 'WAUZZZ8K7BA123456',
    decoded: {
      hersteller: 'Audi AG',
      modell: 'A4 Limousine',
      generation: 'B8 (8K)',
      baujahr: '2011',
      motor: '2.0 TDI 143 PS (CAG)',
      getriebe: '6-Gang Schaltgetriebe',
      antrieb: 'Frontantrieb',
      farbe: 'Brillantschwarz (A2)',
      ausstattung: 'Attraction',
      produktionsort: 'Ingolstadt, Deutschland',
      erstZulassung: '03/2011'
    }
  };

  // Technische Details Demo
  const techSpecsDemo = {
    motor: {
      hubraum: '1968 ccm',
      leistung: '143 PS (105 kW)',
      drehmoment: '320 Nm bei 1750-2500 U/min',
      kraftstoff: 'Diesel',
      verbrauch: '5.1 L/100km (kombiniert)',
      emission: 'Euro 5'
    },
    wartung: {
      oeltyp: '5W-30 VW 507.00',
      oelmenge: '4.5 Liter',
      oelwechsel: 'alle 30.000 km',
      zuendkerzen: 'N/A (Diesel)',
      bremsfluss: 'DOT 4 Plus'
    },
    drehmomente: [
      { teil: 'Radschrauben', wert: '120 Nm' },
      { teil: 'Ölablassschraube', wert: '30 Nm' },
      { teil: 'Zündkerzen', wert: 'N/A' },
      { teil: 'Bremsscheibenhalter', wert: '10 Nm + 90°' }
    ]
  };

  // Reparaturanleitung Demo
  const repairGuideDemo = {
    title: 'Ölwechsel - Audi A4 2.0 TDI',
    duration: '45-60 Minuten',
    difficulty: 'Einfach',
    tools: ['Ölfilter-Schlüssel', '17er Ringschlüssel', 'Ölauffangwanne', 'Trichter'],
    steps: [
      {
        step: 1,
        title: 'Motor warmfahren',
        description: 'Fahren Sie den Motor ca. 5 Minuten warm. Das Öl wird dünnflüssiger und läuft besser ab.',
        warning: 'Vorsicht: Motor und Öl sind heiß!',
        image_description: 'Motor laufen lassen bis Betriebstemperatur erreicht'
      },
      {
        step: 2,
        title: 'Fahrzeug aufbocken',
        description: 'Fahrzeug auf Hebebühne oder mit Wagenheber anheben. Achten Sie auf die korrekten Ansatzpunkte.',
        tip: 'Unterstellböcke zur Sicherheit verwenden',
        image_description: 'Fahrzeug sicher aufgebockt'
      },
      {
        step: 3,
        title: 'Unterbodenverkleidung entfernen',
        description: 'Entfernen Sie die Kunststoffclips und Schrauben der Unterbodenverkleidung.',
        image_description: 'Unterbodenverkleidung gelöst'
      },
      {
        step: 4,
        title: 'Ölablassschraube lösen',
        description: 'Mit 17er Schlüssel die Ölablassschraube lösen. Ölauffangwanne positionieren!',
        warning: 'Heißes Öl! Handschuhe tragen!',
        torque: 'Anzugsdrehmoment: 30 Nm',
        image_description: 'Ölablassschraube wird gelöst'
      },
      {
        step: 5,
        title: 'Ölfilter wechseln',
        description: 'Alten Ölfilter mit Filterschlüssel entfernen. Neuen Filter mit frischem Öl am Dichtring einsetzen.',
        tip: 'Dichtring mit etwas frischem Öl benetzen',
        image_description: 'Ölfilter wird gewechselt'
      }
    ],
    video: {
      title: 'Ölwechsel Audi A4 B8 2.0 TDI - Komplette Anleitung',
      source: 'AUTODOC',
      quality_rating: 5,
      duration: '12:34',
      url: 'https://youtube.com/watch?v=example'
    }
  };

  return (
    <section className="py-24 px-4 relative overflow-hidden" style={{ backgroundColor: '#0B0F14' }}>
      {/* Background Effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 rounded-full opacity-10"
             style={{ background: 'radial-gradient(circle, #1EA0E6 0%, transparent 70%)' }} />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 rounded-full opacity-10"
             style={{ background: 'radial-gradient(circle, #22C55E 0%, transparent 70%)' }} />
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Section Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6" 
               style={{ backgroundColor: '#F59E0B20', border: '1px solid #F59E0B' }}>
            <Sparkles size={20} style={{ color: '#F59E0B' }} />
            <span className="text-sm font-semibold" style={{ color: '#FCD34D' }}>
              PREMIUM FEATURES
            </span>
          </div>
          
          <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: '#E6EDF3', fontFamily: 'Manrope' }}>
            Professionelle Werkzeuge.
            <br />
            <span style={{ color: '#1EA0E6' }}>Für jeden Anwender.</span>
          </h2>
          
          <p className="text-xl max-w-3xl mx-auto" style={{ color: '#94A3B8' }}>
            Von der <strong style={{ color: '#E6EDF3' }}>VIN-Dekodierung</strong> bis zur 
            <strong style={{ color: '#E6EDF3' }}> Video-Reparaturanleitung</strong> – 
            alles was Sie für professionelle Fahrzeugarbeit brauchen.
          </p>
        </motion.div>

        {/* Feature Tabs */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {featureTabs.map((tab) => (
            <motion.button
              key={tab.id}
              onClick={() => setActiveFeature(tab.id)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className={`flex items-center gap-2 px-6 py-3 rounded-full font-semibold transition-all ${
                activeFeature === tab.id 
                  ? 'text-white shadow-lg' 
                  : 'text-gray-400 hover:text-white'
              }`}
              style={{ 
                backgroundColor: activeFeature === tab.id ? tab.color : '#1A1F2E',
                border: `1px solid ${activeFeature === tab.id ? tab.color : '#2A3441'}`
              }}
            >
              <tab.icon size={20} />
              {tab.label}
            </motion.button>
          ))}
        </div>

        {/* Feature Content */}
        <AnimatePresence mode="wait">
          {/* VIN Decoder Feature */}
          {activeFeature === 'vin' && (
            <motion.div
              key="vin"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Left: VIN Input Demo */}
                <Card style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
                  <CardContent className="p-8">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-12 h-12 rounded-xl flex items-center justify-center"
                           style={{ backgroundColor: '#1EA0E620' }}>
                        <QrCode size={24} style={{ color: '#1EA0E6' }} />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold" style={{ color: '#E6EDF3' }}>VIN Decoder</h3>
                        <p className="text-sm" style={{ color: '#94A3B8' }}>Fahrzeug-Identifizierung in Sekunden</p>
                      </div>
                    </div>

                    {/* VIN Input Field */}
                    <div className="mb-6">
                      <label className="block text-sm font-medium mb-2" style={{ color: '#94A3B8' }}>
                        Fahrgestellnummer (VIN)
                      </label>
                      <div className="relative">
                        <input 
                          type="text" 
                          value={vinDecoderDemo.vin}
                          readOnly
                          className="w-full px-4 py-3 rounded-lg text-lg font-mono"
                          style={{ 
                            backgroundColor: '#1A1F2E', 
                            border: '2px solid #1EA0E6',
                            color: '#E6EDF3'
                          }}
                        />
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          <CheckCircle2 size={24} style={{ color: '#22C55E' }} />
                        </div>
                      </div>
                    </div>

                    {/* Decoded Results */}
                    <div className="space-y-3">
                      {Object.entries(vinDecoderDemo.decoded).slice(0, 6).map(([key, value]) => (
                        <div key={key} className="flex justify-between items-center py-2 border-b"
                             style={{ borderColor: '#2A3441' }}>
                          <span className="text-sm capitalize" style={{ color: '#94A3B8' }}>
                            {key.replace(/([A-Z])/g, ' $1').trim()}
                          </span>
                          <span className="font-semibold" style={{ color: '#E6EDF3' }}>{value}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Right: Benefits */}
                <div className="flex flex-col justify-center space-y-6">
                  <h3 className="text-2xl font-bold" style={{ color: '#E6EDF3' }}>
                    Warum VIN Decoder?
                  </h3>
                  
                  <div className="space-y-4">
                    {[
                      { icon: Shield, title: 'Betrugs-Schutz', desc: 'Erkennen Sie manipulierte Tachos oder versteckte Unfallschäden' },
                      { icon: Clock, title: 'Instant-Ergebnisse', desc: 'Vollständige Fahrzeughistorie in unter 5 Sekunden' },
                      { icon: Info, title: 'Komplette Details', desc: 'Motor, Getriebe, Ausstattung, Produktionsort und mehr' },
                      { icon: Zap, title: 'Werkstatt-Integration', desc: 'Automatische Übernahme in Diagnose-Sessions' }
                    ].map((item, idx) => (
                      <div key={idx} className="flex items-start gap-4 p-4 rounded-xl"
                           style={{ backgroundColor: '#1A1F2E' }}>
                        <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                             style={{ backgroundColor: '#1EA0E620' }}>
                          <item.icon size={20} style={{ color: '#1EA0E6' }} />
                        </div>
                        <div>
                          <h4 className="font-semibold mb-1" style={{ color: '#E6EDF3' }}>{item.title}</h4>
                          <p className="text-sm" style={{ color: '#94A3B8' }}>{item.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Technical Specs Feature */}
          {activeFeature === 'specs' && (
            <motion.div
              key="specs"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Motor Specs */}
                <Card style={{ backgroundColor: '#0F141A', borderColor: '#1F2937', borderTop: '4px solid #22C55E' }}>
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <Cog size={24} style={{ color: '#22C55E' }} />
                      <h3 className="text-lg font-bold" style={{ color: '#E6EDF3' }}>Motordaten</h3>
                    </div>
                    <div className="space-y-3">
                      {Object.entries(techSpecsDemo.motor).map(([key, value]) => (
                        <div key={key} className="flex justify-between py-2 border-b" style={{ borderColor: '#2A3441' }}>
                          <span className="text-sm capitalize" style={{ color: '#94A3B8' }}>
                            {key.replace(/([A-Z])/g, ' $1')}
                          </span>
                          <span className="font-medium text-sm" style={{ color: '#E6EDF3' }}>{value}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Wartung */}
                <Card style={{ backgroundColor: '#0F141A', borderColor: '#1F2937', borderTop: '4px solid #1EA0E6' }}>
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <Wrench size={24} style={{ color: '#1EA0E6' }} />
                      <h3 className="text-lg font-bold" style={{ color: '#E6EDF3' }}>Wartungsdaten</h3>
                    </div>
                    <div className="space-y-3">
                      {Object.entries(techSpecsDemo.wartung).map(([key, value]) => (
                        <div key={key} className="flex justify-between py-2 border-b" style={{ borderColor: '#2A3441' }}>
                          <span className="text-sm capitalize" style={{ color: '#94A3B8' }}>
                            {key.replace(/([A-Z])/g, ' $1')}
                          </span>
                          <span className="font-medium text-sm" style={{ color: '#E6EDF3' }}>{value}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Drehmomente */}
                <Card style={{ backgroundColor: '#0F141A', borderColor: '#1F2937', borderTop: '4px solid #F59E0B' }}>
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <Gauge size={24} style={{ color: '#F59E0B' }} />
                      <h3 className="text-lg font-bold" style={{ color: '#E6EDF3' }}>Drehmomente</h3>
                    </div>
                    <div className="space-y-3">
                      {techSpecsDemo.drehmomente.map((item, idx) => (
                        <div key={idx} className="flex justify-between py-2 border-b" style={{ borderColor: '#2A3441' }}>
                          <span className="text-sm" style={{ color: '#94A3B8' }}>{item.teil}</span>
                          <span className="font-bold" style={{ color: '#F59E0B' }}>{item.wert}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Stats Bar */}
              <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { value: '81+', label: 'Hersteller', color: '#1EA0E6' },
                  { value: '1400+', label: 'Generationen', color: '#22C55E' },
                  { value: '1300+', label: 'Motorvarianten', color: '#F59E0B' },
                  { value: '100%', label: 'Datengenauigkeit', color: '#EF4444' }
                ].map((stat, idx) => (
                  <div key={idx} className="text-center p-4 rounded-xl" style={{ backgroundColor: '#1A1F2E' }}>
                    <div className="text-3xl font-bold mb-1" style={{ color: stat.color }}>{stat.value}</div>
                    <div className="text-sm" style={{ color: '#94A3B8' }}>{stat.label}</div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Repair Guide Feature */}
          {activeFeature === 'repair' && (
            <motion.div
              key="repair"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              <Card style={{ backgroundColor: '#0F141A', borderColor: '#1F2937' }}>
                <CardContent className="p-8">
                  {/* Guide Header */}
                  <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8 pb-6 border-b" style={{ borderColor: '#2A3441' }}>
                    <div>
                      <div className="flex items-center gap-3 mb-2">
                        <FileText size={24} style={{ color: '#F59E0B' }} />
                        <h3 className="text-2xl font-bold" style={{ color: '#E6EDF3' }}>
                          {repairGuideDemo.title}
                        </h3>
                      </div>
                      <div className="flex items-center gap-4 text-sm" style={{ color: '#94A3B8' }}>
                        <span className="flex items-center gap-1">
                          <Clock size={16} /> {repairGuideDemo.duration}
                        </span>
                        <span className="px-2 py-1 rounded-full text-xs font-semibold"
                              style={{ backgroundColor: '#22C55E20', color: '#22C55E' }}>
                          {repairGuideDemo.difficulty}
                        </span>
                      </div>
                    </div>
                    
                    {/* Tools Required */}
                    <div className="flex flex-wrap gap-2">
                      {repairGuideDemo.tools.map((tool, idx) => (
                        <span key={idx} className="px-3 py-1 rounded-full text-xs"
                              style={{ backgroundColor: '#1A1F2E', color: '#94A3B8', border: '1px solid #2A3441' }}>
                          {tool}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Steps */}
                  <div className="space-y-6">
                    {repairGuideDemo.steps.map((step, idx) => (
                      <motion.div 
                        key={idx}
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ delay: idx * 0.1 }}
                        className="flex gap-4"
                      >
                        {/* Step Number */}
                        <div className="flex-shrink-0">
                          <div className="w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg"
                               style={{ backgroundColor: '#F59E0B', color: '#0F141A' }}>
                            {step.step}
                          </div>
                          {idx < repairGuideDemo.steps.length - 1 && (
                            <div className="w-0.5 h-full mx-auto mt-2" style={{ backgroundColor: '#2A3441' }} />
                          )}
                        </div>

                        {/* Step Content */}
                        <div className="flex-1 pb-6">
                          <h4 className="text-lg font-semibold mb-2" style={{ color: '#E6EDF3' }}>
                            {step.title}
                          </h4>
                          <p className="mb-3" style={{ color: '#94A3B8' }}>
                            {step.description}
                          </p>
                          
                          {/* Warning */}
                          {step.warning && (
                            <div className="flex items-center gap-2 p-3 rounded-lg mb-2"
                                 style={{ backgroundColor: '#EF444420', border: '1px solid #EF4444' }}>
                              <AlertTriangle size={18} style={{ color: '#EF4444' }} />
                              <span className="text-sm" style={{ color: '#FCA5A5' }}>{step.warning}</span>
                            </div>
                          )}

                          {/* Tip */}
                          {step.tip && (
                            <div className="flex items-center gap-2 p-3 rounded-lg mb-2"
                                 style={{ backgroundColor: '#1EA0E620', border: '1px solid #1EA0E6' }}>
                              <Info size={18} style={{ color: '#1EA0E6' }} />
                              <span className="text-sm" style={{ color: '#7CD4FD' }}>{step.tip}</span>
                            </div>
                          )}

                          {/* Torque */}
                          {step.torque && (
                            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full"
                                 style={{ backgroundColor: '#F59E0B20' }}>
                              <Gauge size={14} style={{ color: '#F59E0B' }} />
                              <span className="text-sm font-semibold" style={{ color: '#FCD34D' }}>{step.torque}</span>
                            </div>
                          )}
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Video Feature */}
          {activeFeature === 'video' && (
            <motion.div
              key="video"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Video Player Mock */}
                <Card style={{ backgroundColor: '#0F141A', borderColor: '#1F2937', overflow: 'hidden' }}>
                  <div className="relative aspect-video bg-black">
                    {/* Video Thumbnail */}
                    <div className="absolute inset-0 flex items-center justify-center"
                         style={{ background: 'linear-gradient(135deg, #1A1F2E 0%, #0F141A 100%)' }}>
                      <div className="text-center">
                        <Car size={80} style={{ color: '#2A3441' }} className="mx-auto mb-4" />
                        <p className="text-sm" style={{ color: '#4A5568' }}>Video-Vorschau</p>
                      </div>
                    </div>
                    
                    {/* Play Button Overlay */}
                    <motion.div 
                      className="absolute inset-0 flex items-center justify-center cursor-pointer"
                      whileHover={{ scale: 1.05 }}
                      onClick={() => setIsVideoPlaying(!isVideoPlaying)}
                    >
                      <div className="w-20 h-20 rounded-full flex items-center justify-center"
                           style={{ backgroundColor: '#EF4444', boxShadow: '0 0 30px rgba(239, 68, 68, 0.5)' }}>
                        <Play size={32} fill="white" style={{ color: 'white', marginLeft: '4px' }} />
                      </div>
                    </motion.div>

                    {/* Video Info Bar */}
                    <div className="absolute bottom-0 left-0 right-0 p-4"
                         style={{ background: 'linear-gradient(transparent, rgba(0,0,0,0.8))' }}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full flex items-center justify-center"
                               style={{ backgroundColor: '#EF4444' }}>
                            <Video size={16} style={{ color: 'white' }} />
                          </div>
                          <div>
                            <p className="font-semibold text-sm" style={{ color: '#E6EDF3' }}>
                              {repairGuideDemo.video.title}
                            </p>
                            <p className="text-xs" style={{ color: '#94A3B8' }}>
                              Quelle: {repairGuideDemo.video.source}
                            </p>
                          </div>
                        </div>
                        <span className="px-3 py-1 rounded-full text-xs font-semibold"
                              style={{ backgroundColor: '#1A1F2E', color: '#94A3B8' }}>
                          {repairGuideDemo.video.duration}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Quality Rating */}
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm" style={{ color: '#94A3B8' }}>Qualitätsbewertung:</span>
                      <div className="flex items-center gap-1">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star 
                            key={star} 
                            size={18} 
                            fill={star <= repairGuideDemo.video.quality_rating ? '#F59E0B' : 'transparent'}
                            style={{ color: star <= repairGuideDemo.video.quality_rating ? '#F59E0B' : '#4A5568' }} 
                          />
                        ))}
                        <span className="ml-2 text-sm font-semibold" style={{ color: '#F59E0B' }}>
                          {repairGuideDemo.video.quality_rating}/5
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Video Benefits */}
                <div className="flex flex-col justify-center space-y-6">
                  <h3 className="text-2xl font-bold" style={{ color: '#E6EDF3' }}>
                    Verifizierte Video-Anleitungen
                  </h3>
                  <p style={{ color: '#94A3B8' }}>
                    Zu jeder Reparaturanleitung finden wir das <strong style={{ color: '#E6EDF3' }}>passende Video</strong> – 
                    geprüft auf Qualität und Übereinstimmung mit Ihrem exakten Fahrzeugmodell.
                  </p>
                  
                  <div className="space-y-4">
                    {[
                      { icon: Shield, title: 'Geprüfte Quellen', desc: 'Nur Videos von AUTODOC, Teilehersteller-Channels und verifizierten Werkstätten', color: '#22C55E' },
                      { icon: Star, title: 'Qualitätsbewertung', desc: 'Jedes Video wird nach Bildqualität, Erklärung und Fahrzeug-Match bewertet', color: '#F59E0B' },
                      { icon: Car, title: 'Modell-Abgleich', desc: 'KI prüft, ob das Video exakt zu Ihrem Fahrzeug und Motor passt', color: '#1EA0E6' }
                    ].map((item, idx) => (
                      <div key={idx} className="flex items-start gap-4 p-4 rounded-xl"
                           style={{ backgroundColor: '#1A1F2E' }}>
                        <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                             style={{ backgroundColor: `${item.color}20` }}>
                          <item.icon size={20} style={{ color: item.color }} />
                        </div>
                        <div>
                          <h4 className="font-semibold mb-1" style={{ color: '#E6EDF3' }}>{item.title}</h4>
                          <p className="text-sm" style={{ color: '#94A3B8' }}>{item.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* CTA Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="text-center mt-16"
        >
          <div className="inline-flex flex-col sm:flex-row gap-4 items-center">
            <Button 
              onClick={onCtaClick}
              className="px-8 py-6 text-lg font-semibold rounded-xl group"
              style={{ 
                background: 'linear-gradient(135deg, #1EA0E6 0%, #0D8ECF 100%)',
                boxShadow: '0 4px 20px rgba(30, 160, 230, 0.3)'
              }}
            >
              Jetzt Plan wählen
              <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
            </Button>
            <span className="text-sm" style={{ color: '#94A3B8' }}>
              ✨ Alle Features in Professional & Enterprise verfügbar
            </span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default PremiumFeaturesShowcase;
